import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import BasicInput from '../components/BasicInput';
import { useToast } from '../components/ToastProvider';
import ConfirmDialog from '../components/ConfirmDialog';
import { submitFeedback } from '../api/feedback';

const ContactPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('contact.errors.name_required');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('contact.errors.email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.errors.email_invalid');
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t('contact.errors.message_required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await submitFeedback(formData.name, formData.email, formData.message);

      // Clear form
      setFormData({
        name: '',
        email: '',
        message: ''
      });

      // Show success dialog
      setShowSuccessDialog(true);

    } catch (error) {
      console.error('Contact form submission failed:', error);
      toast.error(t('contact.error_message'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSuccessDialogConfirm = () => {
    setShowSuccessDialog(false);
    navigate(-1);
  };

  return (
    <div className="container py-8">
      <div className="max-w-[600px] mx-auto bg-white rounded-lg p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-lg font-bold mb-2">{t('contact.title')}</h1>
          <p className="text-sm text-gray-600">{t('contact.description')}</p>
        </div>
        
        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <BasicInput
            title={t('contact.name')}
            type="text"
            placeholder={t('contact.name_placeholder')}
            value={formData.name}
            onChange={handleInputChange('name')}
            error={errors.name}
            required
            maxLength={50}
            containerClassName="w-full"
          />

          <BasicInput
            title={t('contact.email')}
            type="email"
            placeholder={t('contact.email_placeholder')}
            value={formData.email}
            onChange={handleInputChange('email')}
            error={errors.email}
            required
            maxLength={40}
            containerClassName="w-full"
          />

          <div className="flex-col justify-start items-start gap-1 inline-flex w-full">
            <div className="justify-center items-center gap-1 inline-flex">
              <div className="text-[#111111] text-sm font-normal leading-[21px]">
                {t('contact.message')}
              </div>
            </div>
            <div className="relative w-full">
              <textarea
                placeholder={t('contact.message_placeholder')}
                value={formData.message}
                onChange={handleInputChange('message')}
                required
                maxLength={1000}
                rows={6}
                className="w-full rounded-md border text-sm font-normal leading-[21px] outline-none bg-[#f7f7f7] border-[#dddddd] hover:border-[#dddddd] focus:border-[#dddddd] placeholder:text-[#aaaaaa] text-[#111111] px-3 py-3 resize-none"
              />
            </div>
            {errors.message && (
              <div className="self-stretch pt-0.5 justify-start items-center gap-1 inline-flex">
                <div className="grow shrink basis-0 text-[#ff5501] text-xs font-normal leading-[18px]">
                  {errors.message}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="default"
              size="l"
              onClick={handleCancel}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            
            <Button
              type="submit"
              variant="primary"
              size="l"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? t('contact.submitting') : t('contact.submit')}
            </Button>
          </div>
        </form>
      </div>

      {/* Success Dialog */}
      <ConfirmDialog
        isOpen={showSuccessDialog}
        onConfirm={handleSuccessDialogConfirm}
        title={t('contact.success_dialog.title')}
        message={t('contact.success_dialog.message')}
        confirmText={t('common.back')}
        showCancel={false}
      />
    </div>
  );
};

export default ContactPage;
